import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Share2, Search, Sparkles, ArrowLeft, MessageCircle, ExternalLink, Volume2, Pause } from 'lucide-react';
import api from '../../utils/api';
import SponsoredTools from '../../components/SponsoredTools';
import { cardRoman } from './tarotRoman';

const SITE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const TarotGallery = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = React.useRef(null);

  const playCardAudio = (e, card) => {
    e.preventDefault();
    e.stopPropagation();
    // Toggle off if same card is playing
    if (playingId === card.cardId) {
      try { audioRef.current?.pause(); audioRef.current && (audioRef.current.src = ''); } catch (_) {}
      setPlayingId(null);
      return;
    }
    // Stop previous
    try { audioRef.current?.pause(); } catch (_) {}
    const url = `${SITE_URL}/api/anant-tarot/card-tts/${card.cardId}.mp3?lang=en`;
    const audio = new Audio(url);
    audio.volume = 1.0;
    audio.onended = () => setPlayingId(null);
    audio.onerror = () => { setPlayingId(null); };
    audioRef.current = audio;
    setPlayingId(card.cardId);
    audio.play().catch(() => setPlayingId(null));
  };
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [activeSuit, setActiveSuit] = useState('all');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    // SEO meta tags
    document.title = 'Anant Inner Self Tarot Gallery — 79 Unique Spiritual Cards';
    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', name); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    };
    const setOg = (prop, content) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', prop); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    };
    setMeta('description', 'Explore all 79 unique AI-generated Osho-Zen-inspired Tarot cards. Free spiritual gallery with WhatsApp sharing — Anant Inner Self Guidance.');
    setMeta('keywords', 'tarot, osho zen, spiritual gallery, anant tarot, free tarot reading, hindi tarot, 79 tarot cards');
    setOg('og:title', 'Anant Tarot Gallery — 79 Spiritual Cards');
    setOg('og:description', 'Beautifully crafted Osho-Zen-inspired tarot artworks. Tap any card to share its wisdom.');
    setOg('og:type', 'website');
    setOg('og:url', window.location.href);
    setOg('og:image', `${SITE_URL}/api/anant-tarot/card-art/7.png`);

    api.get('/anant-tarot/public-cards')
      .then(r => setCards(r.data.cards || []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, []);

  const suits = useMemo(() => {
    const s = new Set(cards.map(c => c.suit).filter(Boolean));
    return ['all', ...Array.from(s)];
  }, [cards]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter(c => {
      if (activeSuit !== 'all' && c.suit !== activeSuit) return false;
      if (!q) return true;
      return (c.name || '').toLowerCase().includes(q) || (c.keywords || '').toLowerCase().includes(q);
    });
  }, [cards, query, activeSuit]);

  const shareCard = (card) => {
    const url = `${window.location.origin}/tarot/gallery#card-${card.cardId}`;
    const img = `${SITE_URL}${card.image_url}`;
    const text = `🔮 *${card.name}* — Anant Inner Self Tarot\n\n${(card.meaning || 'A spiritual card from the Anant Zen Tarot deck').slice(0, 180)}\n\nExplore all 79 cards 👉 ${url}\n\nGet your personal reading: ${window.location.origin}/anant-tarot`;
    const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(waUrl, '_blank');
  };

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 bg-slate-950" data-testid="tarot-gallery-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 text-sm mb-4" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-3 text-amber-300" data-testid="gallery-title">
            <span className="inline-flex items-center gap-3"><Sparkles className="w-8 h-8" /> Anant Tarot Gallery</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-3xl">
            79 unique Osho-Zen-inspired spiritual cards — hand-crafted via AI, freely viewable, share-friendly.
            Each card carries an ancient lesson for your inner journey.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <Link
              to="/anant-tarot"
              data-testid="get-reading-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400"
            >
              <Sparkles className="w-4 h-4" /> Get Your Personal 5-Card Reading
            </Link>
            <a
              href={`https://wa.me/?text=${encodeURIComponent('🔮 Explore the Anant Inner Self Tarot — 79 beautiful cards waiting for you ' + window.location.href)}`}
              target="_blank" rel="noreferrer"
              data-testid="share-gallery-whatsapp"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-500"
            >
              <MessageCircle className="w-4 h-4" /> Share Gallery on WhatsApp
            </a>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="gallery-search-input"
              type="search"
              placeholder="Search by name or keyword…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 focus:border-amber-400 focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {suits.map(s => (
              <button
                key={s}
                data-testid={`suit-filter-${s.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => setActiveSuit(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${activeSuit === s ? 'bg-amber-500 text-black border-amber-400' : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'}`}
              >
                {s === 'all' ? `All (${cards.length})` : s}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-20 text-slate-400">Loading 79 cards…</div>
        ) : visible.length === 0 ? (
          <div className="text-center py-20 text-slate-400">No cards match your search.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-5">
            {visible.map(card => (
              <Link
                key={card.cardId}
                to={`/tarot/card/${card.cardId}`}
                id={`card-${card.cardId}`}
                data-testid={`gallery-card-${card.cardId}`}
                className="group cursor-pointer rounded-xl overflow-hidden border border-slate-800 bg-slate-900 hover:border-amber-400 relative block"
              >
                <div className="aspect-[2/3] overflow-hidden bg-slate-950 relative">
                  <img
                    src={`${SITE_URL}${card.image_url}`}
                    alt={card.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition duration-500"
                    style={{ filter: 'saturate(1.25) contrast(1.1) brightness(1.05) drop-shadow(0 0 8px rgba(255,200,80,0.25))' }}
                  />
                  {/* Shine sweep on hover */}
                  <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                       style={{ background: 'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)',
                                animation: 'tarot-shine 1.6s ease-in-out infinite' }} />
                  {/* 🔊 Play wisdom button — stops navigation, plays per-card MP3 */}
                  <button
                    type="button"
                    onClick={(e) => playCardAudio(e, card)}
                    data-testid={`card-play-${card.cardId}`}
                    title="Hear this card's wisdom"
                    aria-label={`Play audio meaning for ${card.name}`}
                    className={`absolute bottom-2 right-2 w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition ${playingId === card.cardId
                      ? 'bg-amber-400 text-black scale-110'
                      : 'bg-black/70 text-amber-300 hover:bg-amber-500 hover:text-black opacity-0 group-hover:opacity-100'}`}
                  >
                    {playingId === card.cardId ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </button>
                </div>
                <div className="p-2.5">
                  <div className="text-sm font-bold text-amber-200 truncate">{card.name}</div>
                  {cardRoman(card) && (
                    <div className="text-[11px] font-serif italic tracking-[0.2em] text-amber-400/80" data-testid={`tarot-roman-${card.cardId}`}>{cardRoman(card)}</div>
                  )}
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{card.suit}</div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-12 text-center text-sm text-slate-500">
          Want a personal reading? <Link to="/anant-tarot" className="text-amber-300 underline">Draw your 5-card spread now →</Link>
        </div>

        {/* Sponsored Spiritual Tools — viral revenue stream */}
        <SponsoredTools context="gallery" maxItems={8} />
      </div>

      {/* Card Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/85 p-4 flex items-center justify-center"
          onClick={() => setSelected(null)}
          data-testid="gallery-card-modal"
        >
          <div
            className="max-w-md w-full bg-slate-900 rounded-2xl border border-amber-500/30 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="aspect-[2/3] bg-slate-950">
              <img src={`${SITE_URL}${selected.image_url}`} alt={selected.name} className="w-full h-full object-contain" />
            </div>
            <div className="p-4">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{selected.suit}</div>
              <h2 className="text-2xl font-extrabold text-amber-300 mb-2">{selected.name}</h2>
              {selected.meaning && <p className="text-sm text-slate-200 leading-relaxed mb-3">{selected.meaning}</p>}
              {selected.keywords && <p className="text-xs text-slate-400 italic">Keywords: {selected.keywords}</p>}
              <div className="flex gap-2 mt-4">
                <button
                  data-testid="modal-share-whatsapp"
                  onClick={() => shareCard(selected)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-500"
                >
                  <Share2 className="w-4 h-4" /> Share on WhatsApp
                </button>
                <button
                  data-testid="modal-close"
                  onClick={() => setSelected(null)}
                  className="px-4 py-2.5 rounded-xl bg-slate-800 text-slate-200 hover:bg-slate-700"
                >
                  Close
                </button>
              </div>
              <Link
                to="/anant-tarot"
                data-testid="modal-get-reading"
                className="block mt-3 text-center px-4 py-2.5 rounded-xl bg-amber-500 text-black font-bold hover:bg-amber-400"
              >
                ✨ Draw Your Personal 5-Card Reading
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TarotGallery;
